import type { Destination, ModuleKey } from "./types"

/**
 * Hand-written scorer, no new dependency.
 *
 * Two passes. The phrase pass treats the query as one string, which is what
 * catches "exit requests" landing exactly on its label. The term pass splits it
 * and requires *every* term to match something, which is what makes "payment
 * schedule" and "request approvals" work — neither appears as a literal
 * substring of any label. A destination scores the better of the two.
 *
 * The frecency bonus in README §6 is absent: recents are out of scope, so there
 * is nothing to weight.
 */
export const SCORE = {
  // Phrase pass — the whole query, matched as typed.
  exactLabel: 1000,
  labelPrefix: 800,
  labelWordBoundary: 600,
  keywordExact: 500,
  keywordPrefix: 350,
  subsequence: 150,

  // Term pass — per term, averaged. Deliberately below the phrase tiers: an
  // exact label match should always outrank an every-term match.
  termLabelWord: 400,
  termLabelPrefix: 300,
  termKeywordExact: 250,
  termKeywordPrefix: 180,

  /** Nudge for the module the user is already looking at. */
  currentModule: 200,
  /** Per label word beyond the query, so short labels win on equal footing. */
  perExtraLabelWord: -50,
} as const

/**
 * Case- and whitespace-insensitive, unlike the alias comparison in access.ts.
 * Normalising is right here and wrong there: this is matching text a human
 * typed, not deciding whether someone holds a permission.
 */
const normalize = (value: string) => value.trim().toLowerCase()

const words = (value: string) => value.split(/[\s/&,-]+/).filter(Boolean)

/**
 * Crude de-pluralisation so "schedules" finds "schedule". Only applied to terms
 * long enough that the trailing "s" is unlikely to be part of the word itself.
 */
const singular = (term: string) =>
  term.length > 3 && term.endsWith("s") ? term.slice(0, -1) : term

/** True when every character of `query` appears in `target`, in order. */
export function isSubsequence(query: string, target: string): boolean {
  if (query.length === 0) return false

  let cursor = 0
  for (const char of target) {
    if (char === query[cursor]) cursor += 1
    if (cursor === query.length) return true
  }
  return false
}

function phraseScore(q: string, label: string, keywords: string[]): number {
  if (label === q) return SCORE.exactLabel
  if (label.startsWith(q)) return SCORE.labelPrefix
  if (words(label).some((word) => word.startsWith(q)))
    return SCORE.labelWordBoundary
  if (keywords.some((keyword) => keyword === q)) return SCORE.keywordExact
  if (keywords.some((keyword) => keyword.startsWith(q)))
    return SCORE.keywordPrefix
  if (isSubsequence(q, label)) return SCORE.subsequence
  if (keywords.some((keyword) => isSubsequence(q, keyword)))
    return SCORE.subsequence
  return 0
}

function termScore(
  term: string,
  labelWords: string[],
  keywords: string[]
): number {
  const stem = singular(term)
  const hits = (value: string) => value === term || value === stem
  const startsWith = (value: string) =>
    value.startsWith(term) || value.startsWith(stem)

  if (labelWords.some(hits)) return SCORE.termLabelWord
  if (labelWords.some(startsWith)) return SCORE.termLabelPrefix
  if (keywords.some(hits)) return SCORE.termKeywordExact

  // Keywords are often phrases ("pay schedule"), so their words count too.
  const keywordWords = keywords.flatMap(words)
  if (keywords.some(startsWith) || keywordWords.some(hits))
    return SCORE.termKeywordExact
  if (keywordWords.some(startsWith)) return SCORE.termKeywordPrefix

  return 0
}

/** Every term must match something, or the destination is not a result at all. */
function allTermsScore(
  terms: string[],
  labelWords: string[],
  keywords: string[]
): number {
  if (terms.length === 0) return 0

  let total = 0
  for (const term of terms) {
    const score = termScore(term, labelWords, keywords)
    if (score === 0) return 0
    total += score
  }

  return Math.round(total / terms.length)
}

/**
 * How well a destination matches a query. 0 means no match at all, and callers
 * drop those rather than rendering them — the length penalty can otherwise pull
 * a real match below a non-match.
 */
export function scoreDestination(
  destination: Destination,
  query: string,
  currentModule?: ModuleKey
): number {
  const q = normalize(query)
  if (q.length === 0) return 0

  const label = normalize(destination.label)
  const keywords = destination.keywords.map(normalize)
  const terms = words(q)

  const base = Math.max(
    phraseScore(q, label, keywords),
    allTermsScore(terms, words(label), keywords)
  )

  if (base === 0) return 0

  const boost =
    currentModule !== undefined && destination.module === currentModule
      ? SCORE.currentModule
      : 0

  const extraWords = Math.max(0, words(label).length - terms.length)

  return base + boost + extraWords * SCORE.perExtraLabelWord
}
